import React, { useMemo, useRef, useState } from 'react';
import { MdCheck, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import axios from 'config/axios';
import { useFillAppColleges } from 'hooks/useFillAppColleges';
import {
    type ChecklistItem,
    type College,
    type CollegeChoice,
    TEST_POLICY_LABELS,
    buildChecklistItems,
    combineRequirements,
    deadlineSummary,
    feeLabel,
    formatDeadline,
    recsSummary,
} from 'global/colleges';

// Right panel: one date-grouped checklist — items appear under their
// earliest effective due date (chosen plan's date, else the college's
// earliest deadline), so the list doubles as a priority timeline.
function CombinedPanel({ colleges, plans, checklist, onToggle }: {
    colleges: College[];
    plans: Record<string, string | null | undefined>;
    checklist: Record<string, boolean>;
    onToggle: (key: string) => void;
}) {
    const req = combineRequirements(colleges, plans);
    const items = buildChecklistItems(req);

    const groups = new Map<string | null, ChecklistItem[]>();
    for (const item of items) {
        const key = item.dueBy || null;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(item);
    }
    const dates = [...groups.keys()].filter((d): d is string => !!d).sort();

    const row = (item: ChecklistItem) => {
        const checked = !!checklist[item.key];
        return (
            <button
                key={item.key}
                type='button'
                className={`fappColleges__checkRow ${checked ? 'fappColleges__checkRow--done' : ''}`}
                onClick={() => onToggle(item.key)}
            >
                <span className='fappColleges__checkBox'>{checked && <MdCheck size={13} />}</span>
                <span className='fappColleges__checkText'>
                    <span className='fappColleges__checkLabel'>{item.label}</span>
                    {item.detail && <span className='fappColleges__checkDetail'>{item.detail}</span>}
                </span>
            </button>
        );
    };

    return (
        <aside className='fappColleges__summary'>
            <div className='fappColleges__summaryGroup'>
                <h4>Checklist</h4>
                {dates.map(date => (
                    <div key={date} className='fappColleges__dueGroup'>
                        <div className='fappColleges__dueDate'>Due {formatDeadline(date)}</div>
                        {groups.get(date)!.map(row)}
                    </div>
                ))}
                {groups.has(null) && (
                    <div className='fappColleges__dueGroup'>
                        <div className='fappColleges__dueDate fappColleges__dueDate--none'>No deadline set</div>
                        {groups.get(null)!.map(row)}
                    </div>
                )}
            </div>
        </aside>
    );
}

// "My Colleges" — two panels: left = search/add + the student's list, right =
// the combined requirements roll-up. Choices state lives in the dashboard so
// its hero stat tracks adds/removes.
function CollegesSection({ fillAppId, cycleYear, choices, onChange, checklist, onChecklistChange }: {
    fillAppId: number;
    cycleYear: number;
    choices: CollegeChoice[];
    onChange: (next: CollegeChoice[]) => void;
    checklist: Record<string, boolean>;
    onChecklistChange: (next: Record<string, boolean>) => void;
}) {
    const { grid, loading, error: gridError } = useFillAppColleges(cycleYear);
    const [query, setQuery] = useState('');
    const [saveError, setSaveError] = useState<string | null>(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const searchRef = useRef<HTMLDivElement | null>(null);

    const byId = useMemo(() => {
        const m = new Map<string, College>();
        (grid?.colleges || []).forEach(c => m.set(c.id, c));
        return m;
    }, [grid]);

    const matches = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q || !grid) return [];
        const chosen = new Set(choices.map(c => c.id));
        return grid.colleges
            .filter(c => c.name.toLowerCase().includes(q) && !chosen.has(c.id))
            .slice(0, 8);
    }, [query, grid, choices]);

    const selected = useMemo(
        () => choices.map(ch => byId.get(ch.id)).filter(Boolean) as College[],
        [choices, byId],
    );

    const save = (next: CollegeChoice[], prev: CollegeChoice[]) => {
        onChange(next);
        setSaveError(null);
        axios.post(`fillapp/student/colleges/${fillAppId}/`, { colleges: next.map(c => ({ id: c.id, plan: c.plan || undefined })) })
            .then(res => onChange(res.data.colleges))
            .catch(err => {
                onChange(prev);
                setSaveError(err?.response?.data?.message || 'Failed to save your college list.');
            });
    };

    const add = (college: College) => {
        setQuery('');
        setSearchOpen(false);
        save([...choices, { id: college.id, addedAt: new Date().toISOString() }], choices);
    };

    const remove = (id: string) => {
        save(choices.filter(c => c.id !== id), choices);
    };

    // Reorder (list order = display order, persisted by the same save).
    const move = (id: string, dir: -1 | 1) => {
        const from = choices.findIndex(c => c.id === id);
        const to = from + dir;
        if (from < 0 || to < 0 || to >= choices.length) return;
        const next = [...choices];
        [next[from], next[to]] = [next[to], next[from]];
        save(next, choices);
    };

    // Choose/unchoose how to apply to a college by clicking its deadline chip.
    // Binding Early Decision (ed/edii) is exclusive: guarded here AND server-side.
    const setPlan = (collegeId: string, plan: string | null) => {
        if (plan === 'ed' || plan === 'edii') {
            const other = choices.find(ch => ch.id !== collegeId && (ch.plan === 'ed' || ch.plan === 'edii'));
            if (other) {
                const otherName = byId.get(other.id)?.name || 'Another college';
                setSaveError(`${otherName} is already your Early Decision choice — you can only apply ED to one college.`);
                return;
            }
        }
        setSaveError(null);
        save(choices.map(ch => (ch.id === collegeId ? { ...ch, plan } : ch)), choices);
    };

    const plans = useMemo(
        () => Object.fromEntries(choices.filter(ch => ch.plan).map(ch => [ch.id, ch.plan])),
        [choices],
    );

    // REA is "restrictive": most REA colleges disallow other private early
    // applications. Soft warning only — policies vary by school.
    const reaConflict = useMemo(() => {
        const rea = choices.find(ch => ch.plan === 'rea');
        if (!rea) return null;
        const otherEarly = choices.some(ch => ch.id !== rea.id && ['ed', 'edii', 'ea', 'eaii'].includes(ch.plan || ''));
        return otherEarly ? (byId.get(rea.id)?.name || 'Your REA college') : null;
    }, [choices, byId]);

    // Free two-way toggle (unlike comment resolve): optimistic, persisted,
    // reverted if the save fails.
    const toggleChecklistItem = (key: string) => {
        const prev = checklist;
        const next = { ...checklist };
        if (next[key]) delete next[key]; else next[key] = true;
        onChecklistChange(next);
        axios.post(`fillapp/student/checklist/${fillAppId}/`, { checklist: next })
            .then(res => onChecklistChange(res.data.checklist))
            .catch(() => onChecklistChange(prev));
    };

    return (
        <section className='fappDash__colleges'>
            <div className='fappDash__sectionHead'>
                <h2>My Colleges</h2>
                <span>
                    {choices.length === 0
                        ? 'search for the colleges you plan to apply to'
                        : `${choices.length} college${choices.length > 1 ? 's' : ''} on your list`}
                </span>
            </div>

            <div className={selected.length > 0 ? 'fappColleges__layout' : ''}>
                <div className='fappColleges__main'>
                    <div className='fappColleges__search' ref={searchRef}>
                        <input
                            className='fappColleges__searchInput'
                            type='text'
                            placeholder='Search Common App colleges (e.g. Cornell University)…'
                            value={query}
                            disabled={loading && !grid}
                            onChange={e => { setQuery(e.target.value); setSearchOpen(true); }}
                            onFocus={() => setSearchOpen(true)}
                            onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                        />
                        {searchOpen && matches.length > 0 && (
                            <div className='fappColleges__searchResults'>
                                {matches.map(c => (
                                    <button
                                        key={c.id}
                                        type='button'
                                        className='fappColleges__searchResult'
                                        onMouseDown={e => e.preventDefault()}
                                        onClick={() => add(c)}
                                    >
                                        <span>{c.name}</span>
                                        <span className='fappColleges__searchAdd'>+ Add</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {gridError && <div className='fappColleges__error'>{gridError}</div>}
                    {saveError && <div className='fappColleges__error'>{saveError}</div>}

                    {reaConflict && (
                        <div className='fappColleges__reaWarn'>
                            Heads up: {reaConflict} is Restrictive Early Action — most REA colleges
                            don't allow other private early applications. Check its policy.
                        </div>
                    )}

                    {choices.length > 0 && (
                        <p className='fappColleges__planHint'>
                            Every college defaults to Regular Decision — click a deadline chip to apply early instead.
                        </p>
                    )}

                    {choices.length > 0 && (
                        <div className='fappColleges__list'>
                            {selected.map((c, idx) => {
                                const deadlines = deadlineSummary(c.deadlines);
                                const recs = recsSummary(c.recs);
                                const chosenPlan = plans[c.id] || (c.deadlines.rd ? 'rd' : null);
                                return (
                                    <div key={c.id} className='fappColleges__card'>
                                        <div className='fappColleges__cardTop'>
                                            <h3 className='fappColleges__cardName'>{c.name}</h3>
                                            <span className='fappColleges__cardActions'>
                                                <button
                                                    type='button'
                                                    className='fappColleges__moveBtn'
                                                    title={`Move ${c.name} up`}
                                                    aria-label={`Move ${c.name} up`}
                                                    disabled={idx === 0}
                                                    onClick={() => move(c.id, -1)}
                                                >
                                                    <MdArrowUpward size={14} />
                                                </button>
                                                <button
                                                    type='button'
                                                    className='fappColleges__moveBtn'
                                                    title={`Move ${c.name} down`}
                                                    aria-label={`Move ${c.name} down`}
                                                    disabled={idx === selected.length - 1}
                                                    onClick={() => move(c.id, 1)}
                                                >
                                                    <MdArrowDownward size={14} />
                                                </button>
                                                <button
                                                    type='button'
                                                    className='fappColleges__remove'
                                                    aria-label={`Remove ${c.name}`}
                                                    onClick={() => remove(c.id)}
                                                >
                                                    Remove
                                                </button>
                                            </span>
                                        </div>
                                        <div className='fappColleges__deadlines'>
                                            {deadlines.length > 0
                                                ? deadlines.map(d => (
                                                    <button
                                                        key={d.key}
                                                        type='button'
                                                        className={`fappColleges__deadline fappColleges__deadline--pick ${chosenPlan === d.key ? (d.key === 'rd' ? 'fappColleges__deadline--onRd' : 'fappColleges__deadline--on') : ''}`}
                                                        title={chosenPlan === d.key
                                                            ? (d.key === 'rd' ? 'Applying Regular Decision' : 'Click to revert to Regular Decision')
                                                            : `Apply ${d.label} to ${c.name}`}
                                                        onClick={() => {
                                                            if (d.key === chosenPlan) {
                                                                if (d.key !== 'rd') setPlan(c.id, null);   // back to the RD default
                                                            } else {
                                                                // Explicit RD = the default; store nothing.
                                                                setPlan(c.id, d.key === 'rd' ? null : d.key);
                                                            }
                                                        }}
                                                    >
                                                        <b>{d.label}</b> {d.value}
                                                    </button>
                                                ))
                                                : (
                                                    <span className='fappColleges__deadline'>
                                                        See website for deadlines
                                                    </span>
                                                )}
                                        </div>
                                        <div className='fappColleges__reqs'>
                                            <span className='fappColleges__req'>{feeLabel(c.fees)}{c.fees.us !== c.fees.intl && c.fees.intl != null ? ` ($${c.fees.intl} intl)` : ''}</span>
                                            {c.personalEssay && <span className='fappColleges__req'>Personal essay</span>}
                                            {c.writingSupplement && <span className='fappColleges__req'>Writing supplement</span>}
                                            {c.coursesGrades && <span className='fappColleges__req'>Courses &amp; grades</span>}
                                            {c.portfolio && <span className='fappColleges__req'>Portfolio ({c.portfolio === 'SR' ? 'SlideRoom' : 'college site'})</span>}
                                            {c.testPolicy && <span className='fappColleges__req'>{TEST_POLICY_LABELS[c.testPolicy] || c.testPolicy}</span>}
                                            {recs && <span className='fappColleges__req'>{recs}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {grid && (
                        <p className='fappColleges__source'>
                            Requirements from Common App's official first-year grid
                            {grid.updatedAt ? `, updated ${grid.updatedAt}` : ''}. Always
                            confirm details on the college's website.
                        </p>
                    )}
                </div>

                {selected.length > 0 && (
                    <CombinedPanel colleges={selected} plans={plans} checklist={checklist} onToggle={toggleChecklistItem} />
                )}
            </div>
        </section>
    );
}

export default CollegesSection;
